import React from 'react'
import { getInitials } from '@mote/helpers'
import { Tooltip } from '../TooltipV2'
import {
  StyledAvatar,
  StyledAvatarImage,
  StyledInitials
} from './Avatar.styles'
import type { AvatarSize } from './Common.styles'
import { numericAvatarSizeToCircleSize } from './Common.styles'

interface AvatarProps {
  size?: AvatarSize
  name: string
  src?: string
  bordered?: boolean | number
  alt?: string
  'data-tip'?: boolean
  'data-for'?: string
  className?: string
}

export function AvatarV2(props: AvatarProps): JSX.Element {
  const avatarUrl = React.useMemo(() => {
    if (!props.src) {
      return null
    }

    if (props.src.includes('googleusercontent.com')) {
      const googleUrl = props.src.split('=')

      if (googleUrl.length > 1) {
        return `${googleUrl[0]}=s${numericAvatarSizeToCircleSize(props.size)}-c`
      } else {
        return `${googleUrl[0]}?size=${numericAvatarSizeToCircleSize(
          props.size
        )}`
      }
    }

    return `${props.src}?size=${numericAvatarSizeToCircleSize(props.size)}`
  }, [props.src, props.size])

  return (
    <StyledAvatar
      size={props.size}
      bordered={props.bordered}
      data-tip={props['data-tip']}
      data-for={props['data-for']}
      className={props.className}
    >
      {avatarUrl ? (
        <StyledAvatarImage
          src={avatarUrl}
          referrerPolicy='no-referrer'
          alt={props.alt || props.name}
        />
      ) : (
        <StyledInitials size={props.size} role='img' aria-label={props.name}>
          {getInitials(props.name)}
        </StyledInitials>
      )}
    </StyledAvatar>
  )
}

export interface AvatarWithTooltipProps extends AvatarProps {
  id: string
}

export const AvatarWithTooltip = (
  props: AvatarWithTooltipProps
): JSX.Element => {
  const { id, ...rest } = props

  return (
    <>
      <AvatarV2 {...rest} data-tip data-for={id} />
      <Tooltip id={id} place='top'>
        {props.name}
      </Tooltip>
    </>
  )
}
